import './styles.scss';

import { LinearProgress, Skeleton } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import * as Icons from 'react-icons/fi';
import * as ThemifyIcons from 'react-icons/tfi';
import type { MenuItemStyles } from 'react-pro-sidebar';
import {
  Menu,
  menuClasses,
  MenuItem,
  Sidebar as ProSidebar,
  SubMenu,
} from 'react-pro-sidebar';
import { Link, useLocation } from 'react-router-dom';
import { Badge, Card } from 'reactstrap';

import { hasAnyPermission } from '@/api/admin-access';
import SidebarHeader from '@/components/common/sidebar/sidebar-header';
import IconSVG from '@/components/svg-icon';
import { MENUITEMS } from '@/config/menu';
import { Constants } from '@/constants';
import { AppStore, UserStore } from '@/stores';

import packageJson from '../../../../package.json';

const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatThousands = (num: number) =>
  num >= 1000 ? `${parseFloat((num / 1000).toFixed(1))}k` : num.toString();

type Theme = 'light' | 'dark';

const themes = {
  light: {
    sidebar: {
      backgroundColor: '#ffffff',
      color: '#607489',
    },
    menu: {
      menuContent: '#fbfcfd',
      icon: '#0098e5',
      hover: {
        backgroundColor: '#c5e4ff',
        color: '#44596e',
      },
      disabled: {
        color: '#9fb6cf',
      },
    },
  },
  dark: {
    sidebar: {
      backgroundColor: '#17191d',
      color: '#969799',
    },
    menu: {
      menuContent: '#21242a',
      icon: '#969799',
      hover: {
        backgroundColor: '#2b2f37',
        color: '#969799',
      },
      disabled: {
        color: '#3e5e7e',
      },
    },
  },
};

function findInitialOpenKey(
  items: typeof MENUITEMS,
  activePath: string,
  parentKey = ''
): string | null {
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    const thisKey = parentKey ? `${parentKey}-${i}` : `${i}`;

    if (item.children) {
      if (item.children.some((child) => child.path === activePath)) {
        return thisKey;
      }
      const deeper = findInitialOpenKey(item.children, activePath, thisKey);
      if (deeper) return deeper;
    }
  }
  return null;
}

const Sidebar = () => {
  const [stats, setStats] = useState({
    totalCigars: 0,
    totalVitolas: 0,
    totalVenues: 0,
    totalProducts: 0,
    totalUsers: 0,
  });
  const location = useLocation();
  const [activePath, setActivePath] = useState(location.pathname);
  const [toggled, setToggled] = useState(false);
  const theme: Theme = 'dark';

  const [openKey, setOpenKey] = useState<string | null>(() =>
    findInitialOpenKey(MENUITEMS, activePath)
  );

  const handleToggle = (key: string, isOpen: boolean) => {
    setOpenKey(isOpen ? key : null);
  };

  const showSidebar = AppStore.useState((s) => s.showSidebar);
  const access = UserStore.useState((s) => s.access);
  const loadingAccess = UserStore.useState((s) => s.loadingAccess);

  const renderMenuSkeleton = () => (
    <div
      className="sidebar-menu-skeleton"
      aria-label="Loading available navigation"
    >
      {Array.from({ length: 7 }, (_item, index) => (
        <div className="sidebar-menu-skeleton-item" key={index}>
          <Skeleton animation="wave" variant="rounded" />
          {index < 2 && (
            <Skeleton
              animation="wave"
              className="sidebar-menu-skeleton-child"
              variant="rounded"
            />
          )}
        </div>
      ))}
    </div>
  );

  useEffect(() => {
    setToggled(showSidebar);
  }, [showSidebar]);

  useEffect(() => {
    const path = location.pathname;
    setOpenKey(findInitialOpenKey(MENUITEMS, location.pathname));
    setActivePath(path);
  }, [location.pathname]);

  const toggleSidebar = (isOpen) => {
    AppStore.update((s) => {
      s.showSidebar = isOpen;
    });
  };

  const menuItemStyles: MenuItemStyles = {
    root: {
      fontSize: '13px',
      fontWeight: 400,
    },
    icon: {
      color: themes[theme].menu.icon,
      [`&.${menuClasses.disabled}`]: {
        color: themes[theme].menu.disabled.color,
      },
    },
    SubMenuExpandIcon: {
      color: '#b6b7b9',
    },
    subMenuContent: ({ level }) => ({
      backgroundColor:
        level === 0
          ? hexToRgba(themes[theme].menu.menuContent, 1)
          : 'transparent',
    }),
    button: {
      [`&.${menuClasses.disabled}`]: {
        color: themes[theme].menu.disabled.color,
      },
      '&:hover': {
        backgroundColor: hexToRgba(themes[theme].menu.hover.backgroundColor, 1),
        color: themes[theme].menu.hover.color,
      },
    },
    label: ({ open }) => ({
      fontWeight: open ? 600 : undefined,
    }),
  };

  useEffect(() => {
    axios
      .get(`${Constants.apiPath}/stats/totals`)
      .then((res) => {
        setStats({
          totalCigars: res.data.cigar_count,
          totalVitolas: res.data.vitola_count,
          totalVenues: res.data.venue_count,
          totalProducts: res.data.product_count,
          totalUsers: res.data.user_count,
        });
      })
      .catch((e) => {
        console.log(e.message);
      });
  }, []);

  const renderIcon = (menuItem: any) => {
    if (menuItem.iconType === 'component') {
      const IconName = Icons[menuItem.icon];
      return <IconName />;
    }
    if (menuItem.iconType === 'svg') {
      return <IconSVG icon={menuItem.icon} style={{ height: 24, width: 24 }} />;
    }
    if (menuItem.icon) {
      try {
        const Icon = ThemifyIcons[menuItem.icon];
        // console.log('Rendering icon: ', menuItem.icon);
        return <Icon />;
      } catch (error) {
        console.error(`Icon not found for ${menuItem.icon}`);
      }
    }
    return null;
  };

  const handleItemClick = (_path: string) => {
    if (isMobile) {
      toggleSidebar(false);
    }
  };

  const replacePlaceholder = (value: string) => {
    if (value.indexOf('{{') !== -1 && value.indexOf('}}') !== -1) {
      const param = value.replace('{{', '').replace('}}', '').split('.')[1];
      return formatThousands(stats[param] || 0);
    }
    return value;
  };

  const renderMenuItems = (
    items: any[],
    parentKey = '',
    inheritedPermission?: string | string[]
  ) => {
    return items.map((menuItem, index) => {
      const key = parentKey ? `${parentKey}-${index}` : `${index}`;
      const permission = menuItem.permissionKey || inheritedPermission;
      const permissionKeys = Array.isArray(permission)
        ? permission
        : [permission].filter(Boolean);
      const permissionAction = menuItem.type === 'action' ? 'write' : 'read';
      if (
        !loadingAccess &&
        permissionKeys.length > 0 &&
        !hasAnyPermission(access, permissionKeys, permissionAction)
      ) {
        return null;
      }

      return (
        <React.Fragment key={key}>
          {menuItem.sidebartitle && (
            <div className="sidebar-title">{menuItem.sidebartitle}</div>
          )}
          {menuItem.type === 'title' && menuItem.children && (
            <SubMenu
              open={openKey === key}
              onOpenChange={(isOpen) => handleToggle(key, isOpen)}
              label={menuItem.title}
              style={{ fontSize: 14, fontWeight: 600 }}
              suffix={
                menuItem.badgeValue &&
                // @ts-ignore
                // eslint-disable-next-line eqeqeq
                replacePlaceholder(menuItem.badgeValue) != 0 && (
                  <Badge
                    color={menuItem.badgeType || 'dark'}
                    className={menuItem.badgeStyle}
                  >
                    {menuItem.badgeStyle !== 'dot' &&
                      replacePlaceholder(menuItem.badgeValue)}
                  </Badge>
                )
              }
            >
              {renderMenuItems(menuItem.children, key, permission)}
            </SubMenu>
          )}
          {menuItem.type === 'sub' && menuItem.children && (
            <SubMenu label={menuItem.title} icon={renderIcon(menuItem)}>
              {renderMenuItems(menuItem.children, key, permission)}
            </SubMenu>
          )}
          {menuItem.type === 'link' &&
            (menuItem.path.indexOf('http') !== -1 ? (
              <MenuItem
                icon={renderIcon(menuItem)}
                active={activePath === menuItem.path}
                onClick={() => handleItemClick(menuItem.path)}
                component={
                  <a
                    href={menuItem.path}
                    target="_blank"
                    rel="noopener noreferrer"
                  />
                }
              >
                {menuItem.title}
              </MenuItem>
            ) : (
              <MenuItem
                icon={renderIcon(menuItem)}
                active={activePath === menuItem.path}
                onClick={() => handleItemClick(menuItem.path)}
                component={
                  <Link to={`${process.env.PUBLIC_URL}${menuItem.path}`} />
                }
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    width: '100%',
                  }}
                >
                  <div style={{ flex: 1 }}>{menuItem.title}</div>
                  {menuItem.badgeValue &&
                    // @ts-ignore
                    // eslint-disable-next-line eqeqeq
                    replacePlaceholder(menuItem.badgeValue) != 0 && (
                      <Badge color={menuItem.badgeType || 'dark'}>
                        {replacePlaceholder(menuItem.badgeValue)}
                      </Badge>
                    )}
                </div>
              </MenuItem>
            ))}
          {menuItem.type === 'action' && (
            <MenuItem icon={renderIcon(menuItem)} onClick={menuItem.onClick}>
              {menuItem.title}
            </MenuItem>
          )}
        </React.Fragment>
      );
    });
  };

  // Compute user progress metrics.
  let goal = 10000;
  if (stats.totalUsers > 100000) {
    goal = stats.totalUsers;
  } else if (stats.totalUsers > 50000) {
    goal = 100000;
  } else if (stats.totalUsers > 25000) {
    goal = 50000;
  } else if (stats.totalUsers > 10000) {
    goal = 25000;
  }
  const userPercent = parseInt((stats.totalUsers / goal) * 100, 10);

  return (
    <ProSidebar
      collapsed={false}
      breakPoint="md"
      onBackdropClick={() => toggleSidebar(false)}
      toggled={isMobile ? toggled : false}
      backgroundColor={hexToRgba(themes[theme].sidebar.backgroundColor, 1)}
      rootStyles={{
        color: themes[theme].sidebar.color,
      }}
    >
      <SidebarHeader />
      {loadingAccess ? (
        renderMenuSkeleton()
      ) : (
        <Menu menuItemStyles={menuItemStyles}>
          {renderMenuItems(MENUITEMS)}
        </Menu>
      )}
      <Card
        style={{
          backgroundColor: '#21242a',
          color: '#d9d9d9',
          margin: 20,
          padding: 10,
        }}
      >
        <div style={{ display: 'flex' }}>
          <span style={{ flex: 1, fontSize: 11 }}>Total Cigars:</span>
          <span style={{ fontSize: 11 }}>
            {parseInt(stats.totalCigars, 10).toLocaleString()}
          </span>
        </div>
        <div style={{ display: 'flex' }}>
          <span style={{ flex: 1, fontSize: 11 }}>Total Vitolas:</span>
          <span style={{ fontSize: 11 }}>
            {parseInt(stats.totalVitolas, 10).toLocaleString()}
          </span>
        </div>
        <div style={{ display: 'flex' }}>
          <span style={{ flex: 1, fontSize: 11 }}>Total Venues:</span>
          <span style={{ fontSize: 11 }}>
            {parseInt(stats.totalVenues, 10).toLocaleString()}
          </span>
        </div>
        <div style={{ display: 'flex' }}>
          <span style={{ flex: 1, fontSize: 11 }}>Total Products:</span>
          <span style={{ fontSize: 11 }}>
            {parseInt(stats.totalProducts, 10).toLocaleString()}
          </span>
        </div>
        <hr style={{ borderColor: '#636467' }} />
        <div>
          <span style={{ display: 'block', fontSize: 11, marginBottom: 12 }}>
            {`User count: ${parseInt(stats.totalUsers, 10).toLocaleString()} / ${formatThousands(goal)} (${userPercent}%)`}
          </span>
          <LinearProgress
            variant="determinate"
            color="secondary"
            value={userPercent}
          />
        </div>
      </Card>
      <div style={{ textAlign: 'center', padding: 16 }}>
        <span
          style={{
            fontSize: 11,
            color: 'rgba(255, 255, 255, 0.7)',
          }}
        >
          {`© ${new Date().getFullYear()} Boxpressd Ltd.`}
          <br />
          {`All rights reserved. v${packageJson.version}`}
        </span>
      </div>
    </ProSidebar>
  );
};

export default Sidebar;
